/**
 * NumberCell Component
 *
 * Renders numeric values with optional unit display and formatting.
 * Supports inline editing with number validation.
 */

import { useState, useRef, useEffect } from 'react';
import type { NumbersCellProps } from './types';

export function NumberCell({
  value,
  onChange,
  unit,
  decimals = 2,
  format = 'number',
  readonly = false,
}: NumbersCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value?.value?.toString() ?? '');
  }, [value?.value]);

  const formatNumber = (num: number): string => {
    const options: Intl.NumberFormatOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    };

    switch (format) {
      case 'currency':
        options.style = 'currency';
        options.currency = 'USD';
        break;
      case 'percentage':
        options.style = 'percent';
        options.minimumFractionDigits = 0;
        options.maximumFractionDigits = decimals;
        // Note: percent style multiplies by 100, so we assume value is already in percent form (e.g., 50 for 50%)
        return (num / 100).toLocaleString(undefined, options);
      default:
        break;
    }

    return num.toLocaleString(undefined, options);
  };

  const handleSubmit = () => {
    setIsEditing(false);
    if (!onChange) return;

    const trimmed = editValue.trim();
    if (trimmed === '') {
      onChange(null);
      return;
    }

    const parsed = parseFloat(trimmed);
    if (!isNaN(parsed)) {
      // Round to specified decimals
      const rounded = Math.round(parsed * Math.pow(10, decimals)) / Math.pow(10, decimals);
      onChange({
        value: rounded,
        unit: unit,
      });
    } else {
      // Invalid number - revert
      setEditValue(value?.value?.toString() ?? '');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(value?.value?.toString() ?? '');
    }
  };

  const displayUnit = unit || value?.unit;

  if (readonly) {
    return (
      <div className="w-full h-full flex items-center">
        {value?.value !== undefined ? (
          <span className="text-sm text-gray-700">
            {formatNumber(value.value)}
            {displayUnit && format === 'number' && ` ${displayUnit}`}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="number"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSubmit}
          onKeyDown={handleKeyDown}
          step={Math.pow(10, -decimals)}
          className="w-full text-sm bg-white border border-primary-500 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        {displayUnit && format === 'number' && (
          <span className="text-sm text-gray-500 whitespace-nowrap">{displayUnit}</span>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      className="w-full h-full flex items-center cursor-text min-h-[28px]"
    >
      {value?.value !== undefined ? (
        <span className="text-sm text-gray-700">
          {formatNumber(value.value)}
          {displayUnit && format === 'number' && ` ${displayUnit}`}
        </span>
      ) : (
        <span className="text-gray-400 text-sm hover:text-gray-500">-</span>
      )}
    </div>
  );
}
